import { motion, AnimatePresence } from 'framer-motion'
import React, { useState, useRef } from 'react'
import { useClickAway } from 'react-use'
import styled, { css } from 'styled-components'
import { Cross } from 'icons/Cross'
import { media } from 'utils/media'
import { useEscapeKey } from 'utils/useEscapeKey'
import { Button, Props as ActionButtonProps } from 'lib/Button/Button'
import { Text } from 'lib/Text'
import { useTippyPopoverController } from './TippyPopover'

export type Placement = 'left' | 'right'
type ButtonAlign = 'center' | 'flex-start' | 'flex-end'

const OuterContainer = styled.div`
  position: relative;

  ${media.phoneDown} {
    width: 100%;
  }

  & + & {
    ${media.phoneUp} {
      margin-left: 12px;
    }

    ${media.phoneDown} {
      margin-top: 12px;
    }
  }
`

const Container = styled(motion.div)<{ placement: Placement }>`
  position: absolute;
  background: white;
  border: 1px solid var(--black-15);
  box-shadow: 0px 4px 8px 2px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  top: 0;
  width: max-content;
  min-width: 250px;
  display: flex;
  flex-direction: column;

  ${(props) =>
    props.placement === 'left' &&
    css`
      left: 0;
    `}

  ${(props) =>
    props.placement === 'right' &&
    css`
      right: 0;
    `}

  ${media.phoneUp} {
    transform-origin: top right;
    transform-style: preserve-3d;
  }

  ${media.phoneDown} {
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    border: none;
    border-radius: 0;
    box-shadow: none;
    width: 100%;
  }
`

const Content = styled.div`
  padding: 21px 16px;
  max-height: 50vh;
  overflow: auto;

  ${media.phoneDown} {
    padding-top: 80px;
    padding-bottom: 100px;
    max-height: 100vh;
    border-top: 1px solid var(--black-15);
  }
`

const Modal = styled.div<{ placement: Placement }>`
  position: absolute;
  margin-top: 12px;
  z-index: 50;
  display: flex;
  flex-direction: column;

  ${(props) =>
    props.placement === 'left' &&
    css`
      left: 0;
    `}

  ${(props) =>
    props.placement === 'right' &&
    css`
      right: 0;
    `}

  ${media.phoneUp} {
    perspective: 500px;
    perspective-origin: top left;
  }

  ${media.phoneDown} {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 500;
    margin-top: 0;
  }
`

const CloseButton = styled.button`
  align-items: center;
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  height: 40px;
  justify-content: center;
  padding: 0;
  width: 40px;
`

const TopSection = styled.div`
  align-items: center;
  background: white;
  border-bottom: solid 1px var(--black-80);
  display: flex;
  height: 61px;
  justify-content: space-between;
  left: 0;
  padding: 0 16px;
  position: fixed;
  right: 0;
  top: 0;
  touch-action: none;
  z-index: 10;

  ${media.phoneUp} {
    display: none;
  }
`

const Title = styled(Text)`
  flex: 1;
  margin-right: 24px;
  margin-bottom: 24px;

  ${media.phoneDown} {
    margin-bottom: -2px;
  }
`

const Actions = styled.div`
  border-top: 1px solid var(--black-15);
  padding: 8px 8px 8px 16px;
  touch-action: none;

  ${media.phoneDown} {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    z-index: 100;
  }
`

const TriggerButton = styled(Button)<{ align: ButtonAlign }>`
  white-space: nowrap;

  ${(props) =>
    css`
      justify-content: ${props.align ? props.align : 'center'};
    `}

  ${media.phoneDown} {
    width: 100%;
  }
`

const CrossIcon = styled(Cross)`
  width: 20px;

  &:hover path {
    fill: black;
  }
`

export const usePopoverController = () => {
  const [open, setOpen] = useState<boolean>(false)

  return {
    open,
    setOpen
  }
}

interface Props {
  className?: string
  title: string
  children: React.ReactNode
  button: ActionButtonProps & { align?: ButtonAlign }
  placement?: Placement
  controls: ReturnType<typeof useTippyPopoverController>
  renderActions?: React.ReactNode
  hideTopSection?: boolean
  backButton?: React.ReactNode
  closeButton?: React.ReactNode
}

export const Popover = (props: Props) => {
  const {
    className,
    children,
    title,
    button,
    controls: { open, setOpen },
    placement = 'left',
    renderActions,
    closeButton,
    backButton
  } = props
  const contentRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEscapeKey(() => setOpen(false), open)

  useClickAway(contentRef, (e) => {
    if (e.target !== buttonRef.current) {
      setOpen(false)
    }
  })

  return (
    <OuterContainer className={className}>
      <TriggerButton align={button.align || 'center'} ref={buttonRef} active={open} onClick={() => setOpen((prev) => !prev)} {...button} />

      <AnimatePresence>
        {open && (
          <Modal placement={placement}>
            <Container placement={placement} initial={{ rotateX: 5 }} animate={{ rotateX: 0 }} exit={{ rotateX: 5, opacity: 0 }} ref={contentRef}>
              <Content>
                <TopSection>
                  {React.isValidElement(backButton) && backButton}

                  {title && (
                    <Title weight="semi" size="p">
                      {title}
                    </Title>
                  )}

                  {React.isValidElement(closeButton) ? (
                    closeButton
                  ) : (
                    <CloseButton aria-label="Close" onClick={() => setOpen(false)}>
                      <CrossIcon fill="black" />
                    </CloseButton>
                  )}
                </TopSection>

                {children}
              </Content>
              {renderActions && <Actions>{renderActions}</Actions>}
            </Container>
          </Modal>
        )}
      </AnimatePresence>
    </OuterContainer>
  )
}
